/**
 * Coach — full-page home for The Coach. Left rail lists every past
 * conversation (coaching often spans projects); main area is the chat.
 * New sessions can optionally attach a script so the coach can read the draft.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/Header";
import { TrialBanner } from "@/components/TrialBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { CoachChat } from "@/components/coach/CoachChat";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Compass, Plus, Trash2, FileText, ChevronDown } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

export default function Coach() {
  const confirm = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [scripts, setScripts] = useState([]);

  useEffect(() => {
    api.get("/coach/sessions").then(({ data }) => {
      const list = data.sessions || [];
      setSessions(list);
      if (list[0]) setSelectedId(list[0].id);
    }).catch(() => {});
    api.get("/scripts").then(({ data }) => setScripts(data.scripts || [])).catch(() => {});
  }, []);

  const createSession = async (scriptId) => {
    setNewOpen(false);
    try {
      const { data } = await api.post("/coach/sessions", { script_id: scriptId || null });
      setSessions((prev) => [data.session, ...prev]);
      setSelectedId(data.session.id);
    } catch { toast.error("Couldn't start a session."); }
  };

  const ensureSession = useCallback(async () => {
    const { data } = await api.post("/coach/sessions", { script_id: null });
    setSessions((prev) => [data.session, ...prev]);
    return data.session.id;
  }, []);

  const onTitle = useCallback((title, sid) => {
    setSessions((prev) => prev.map((s) => (s.id === sid ? { ...s, title } : s)));
    setSelectedId((cur) => cur || sid);
  }, []);

  const deleteSession = async (s) => {
    const ok = await confirm({ title: "Delete this conversation?", description: "The Coach's session notes for it go too.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/coach/sessions/${s.id}`);
      setSessions((prev) => {
        const next = prev.filter((x) => x.id !== s.id);
        setSelectedId((cur) => (cur === s.id ? (next[0]?.id ?? null) : cur));
        return next;
      });
    } catch { toast.error("Couldn't delete that conversation."); }
  };

  const selected = sessions.find((s) => s.id === selectedId) || null;

  return (
    <div className="min-h-screen bg-[var(--canvas)] flex flex-col">
      <ImpersonationBanner />
      <TrialBanner />
      <AppHeader />
      <div className="flex-1 min-h-0 max-w-[1400px] w-full mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row gap-5" data-testid="coach-page">
        {/* sessions rail */}
        <aside className="w-full sm:w-[280px] shrink-0 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-display text-xl text-[var(--ink-primary)] flex items-center gap-2"><Compass size={19} className="text-[var(--brand)]" /> The Coach</h1>
          </div>
          <div className="relative mb-3">
            <button onClick={() => setNewOpen((o) => !o)} className="btn-brand w-full text-sm inline-flex items-center justify-center gap-1.5 py-2" data-testid="coach-page-new-session">
              <Plus size={14} /> New session <ChevronDown size={13} />
            </button>
            {newOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[var(--line)] rounded-lg shadow-xl py-1 z-30 max-h-64 overflow-auto" data-testid="coach-new-menu">
                <button onClick={() => createSession(null)} className="w-full text-left px-3 py-2 hover:bg-[var(--warm-accent)] text-sm" data-testid="coach-new-general">General coaching</button>
                {scripts.length > 0 && <p className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] px-3 pt-2 pb-1">About a script</p>}
                {scripts.map((s) => (
                  <button key={s.id} onClick={() => createSession(s.id)} className="w-full text-left px-3 py-2 hover:bg-[var(--warm-accent)] text-sm flex items-center gap-2" data-testid={`coach-new-script-${s.id}`}>
                    <FileText size={12} className="text-[var(--ink-muted)] shrink-0" /> <span className="truncate">{s.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-1 max-h-[30vh] sm:max-h-none" data-testid="coach-session-list">
            {sessions.length === 0 && (
              <p className="text-sm text-[var(--ink-muted)] px-1 py-3" data-testid="coach-sessions-empty">Your conversations will live here.</p>
            )}
            {sessions.map((s) => (
              <div key={s.id} className={`group flex items-start gap-2 rounded-lg px-3 py-2 cursor-pointer border ${s.id === selectedId ? "bg-[var(--paper)] border-[var(--line)]" : "border-transparent hover:bg-[var(--warm-accent)]"}`} onClick={() => setSelectedId(s.id)} data-testid={`coach-page-session-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[var(--ink-primary)] truncate">{s.title}</div>
                  <div className="flex items-center gap-2 text-[10px] text-[var(--ink-muted)] mt-0.5">
                    {fmtDate(s.updated_at)}
                    {s.script_title && <span className="inline-flex items-center gap-1 truncate"><FileText size={9} /> {s.script_title}</span>}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s); }} className="opacity-0 group-hover:opacity-100 p-1 text-[var(--ink-muted)] hover:text-[var(--err)] transition-opacity shrink-0" data-testid={`coach-delete-session-${s.id}`} title="Delete">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* chat */}
        <main className="flex-1 min-h-0 flex flex-col bg-[var(--paper)] border border-[var(--line)] rounded-xl overflow-hidden min-h-[60vh]">
          <div className="px-4 py-3 border-b border-[var(--line)] shrink-0">
            <p className="font-display text-base text-[var(--ink-primary)] truncate" data-testid="coach-page-title">{selected ? selected.title : "New conversation"}</p>
            <p className="text-[11px] text-[var(--ink-muted)]">Draws the story out of you — never writes it for you.{selected?.script_title ? ` · Reading: ${selected.script_title}` : ""}</p>
          </div>
          <CoachChat key={selectedId || "new"} sessionId={selectedId} ensureSession={ensureSession} onTitle={onTitle} />
        </main>
      </div>
    </div>
  );
}
